import { useEffect, useState } from 'react';
import {
  defaultGuid, useDidUpdate, useIsMsalAuth, useIsUserAuth,
} from '@equifinance/shared';
import _ from 'lodash';
import useCreditApi from '../../api/useCreditApi';
import {
  Credit,
  CreditItem,
  CreditTypes,
  CreditTypesMap,
  Insolvency,
} from './Credit';
import { defaultCredit } from './defaultCredit';
import { useCreditContext } from './CreditProvider';
import { useAccountContext } from '../account';
import { Stages } from '../../types/Stages';
import { useProgressStatusContext } from '../progressStatus/ProgressStatusProvider';
import { useProductsContext } from '../products';

export function useCredit(id: string) {
  const [credit, setCredit] = useState<Credit>(defaultCredit);
  const isInternalUser = useIsMsalAuth();
  const isExternalUser = useIsUserAuth();

  const { account, creditSearchCompleted, setProductsResultDigest } = useAccountContext();
  const { setInProgress } = useProgressStatusContext();

  const incrementedUpdateCount = (prevUpdateCount: number) => (prevUpdateCount ? prevUpdateCount + 1 : 1);

  const updateCredit = async (field: string, value: any, disableIncrementUpdateCount: boolean = false, initiator: string = '') => {
    setInProgress(initiator, true);

    setCredit((prev) => {
      const clone = _.cloneDeep(prev);
      const newCredit = _.set(clone, field, value);
      return {
        ...newCredit,
        updateCount: (disableIncrementUpdateCount ? prev?.updateCount : incrementedUpdateCount(prev?.updateCount)),
        dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
        initiator,
      };
    });
  };

  const DeleteFromCredit = async (field: string, itemId: string) => {
    setCredit((prev) => {
      const clone = _.cloneDeep(prev);
      const obj = _.get(clone, field);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [itemId]: _deletedItem, ...rest } = obj;
      const newCredit = _.set(clone, field, rest);

      return {
        ...newCredit,
        updateCount: incrementedUpdateCount(prev?.updateCount),
        dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
      };
    });
  };

  const {
    fetch, del,
  } = useCreditApi();

  const fetchCredit = async () => {
    const result = await fetch(id);
    setProductsResultDigest(result.productsResultDigest);
    setCredit((prev) => ({
      ...prev,
      ...result,
    }));
  };

  useEffect(() => {
    if ((isInternalUser && creditSearchCompleted) || (isExternalUser && account.stage !== Stages.CreditDecision)) {
      (async () => {
        await fetchCredit();
      })();
    }
  }, [id, account.stage, creditSearchCompleted, isInternalUser, isExternalUser]);

  const creditTypes = {
    securedCredit: credit?.securedCreditItems?.creditItems,
    unsecuredCredit: credit?.unsecuredCreditItems?.creditItems,
  };

  const creditAddressTypes = {
    securedCreditAddresses: credit?.addresses,
    unsecuredCreditAddresses: credit?.addresses,
    insolvencyAddresses: credit?.addresses,
  };

  return {
    credit,
    ...creditTypes,
    ...creditAddressTypes,
    setCreditData: setCredit,
    updateCredit,
    fetchCredit,
    async appendToCreditArray<T>(field: string, value: T) {
      setCredit((prev) => {
        const clone = _.cloneDeep(prev);
        const currentArray = _.get(clone, field) ?? [];

        const newCredit = _.set(clone, field, [...currentArray, value]);
        return {
          ...newCredit,
          updateCount: incrementedUpdateCount(prev?.updateCount),
          dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
        };
      });
    },
    async deleteFromCreditArray(field: string, itemId: string) {
      setCredit((prev) => {
        const clone = _.cloneDeep(prev);
        const arr = _.get(clone, field);
        const newArray = arr.filter((item: { id: string }) => item.id !== itemId);
        const newCredit = _.set(clone, field, newArray);

        return {
          ...newCredit,
          updateCount: incrementedUpdateCount(prev?.updateCount),
          dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
        };
      });
    },

    async addCreditItem(data: Partial<CreditItem>, type: CreditTypes, initiator: string = '') {
      const creditItemsProperty = CreditTypesMap[type];
      updateCredit(`${creditItemsProperty}.creditItems.${defaultGuid}`, data, false, initiator);
    },
    async updateCreditItem(data: Partial<CreditItem>, creditItemId: string, type: CreditTypes, disableIncrementUpdateCount: boolean = false) {
      const creditItemsProperty = CreditTypesMap[type];
      updateCredit(`${creditItemsProperty}.creditItems.${creditItemId}`, {
        ...credit[creditItemsProperty].creditItems?.[creditItemId],
        ...data,
      }, disableIncrementUpdateCount);
    },
    async deleteCreditItems(creditItem: CreditItem, type: CreditTypes) {
      const creditItemsProperty = CreditTypesMap[type];
      const field = `${creditItemsProperty}.creditItems`;
      await del(credit?.id, { [creditItemsProperty]: [creditItem?.id] });
      DeleteFromCredit(field, creditItem?.id);
    },
    async addInsolvencyItem(data: Partial<Insolvency>, initiator: string = '') {
      updateCredit(`insolvencyRecords.insolvencyRecords.${defaultGuid}`, data, false, initiator);
    },
    async updateInsolvencyItem(data: Partial<Insolvency>, insolvencyItemId: string, disableIncrementUpdateCount: boolean = false) {
      updateCredit(`insolvencyRecords.insolvencyRecords.${insolvencyItemId}`, {
        ...credit.insolvencyRecords.insolvencyRecords[insolvencyItemId],
        ...data,
      }, disableIncrementUpdateCount);
    },
    async deleteInsolvencyItems(insolvencyItem: Insolvency) {
      const field = 'insolvencyRecords.insolvencyRecords';
      await del(credit?.id, { insolvencyRecords: [insolvencyItem?.id] });
      DeleteFromCredit(field, insolvencyItem?.id);
    },
  };
}

export const useCreditAutoSave = () => {
  const didUpdate = useDidUpdate();
  const { id, credit, setCreditData } = useCreditContext();
  const { fetchProductLoanDetails } = useProductsContext();
  const { setProductsResultDigest, fetchTransitionStatus } = useAccountContext();
  const { setInProgress } = useProgressStatusContext();

  const { update } = useCreditApi();

  useEffect(() => {
    if (!didUpdate) return;
    (async () => {
      const { updateCount, dirtyCount, initiator } = credit;

      const newCredit = await update(id, {
        securedCreditItems: Object.values(credit.securedCreditItems.creditItems),
        unsecuredCreditItems: Object.values(credit.unsecuredCreditItems.creditItems),
        insolvencyRecords: Object.values(credit.insolvencyRecords.insolvencyRecords),
      });

      setInProgress(initiator, false);
      await fetchTransitionStatus();

      const { productsResultDigest } = newCredit;
      setProductsResultDigest(productsResultDigest);

      setCreditData((prev: Credit) => {
        if (updateCount >= prev?.updateCount && dirtyCount >= prev?.dirtyCount) {
          return {
            ...newCredit,
            updateCount,
          };
        }
        return prev;
      });

      await fetchProductLoanDetails();
    })();
  }, [credit?.updateCount]);
};
